import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';

import type { Criteria } from '@nbx/core/http';

import { delay, mergeMap, of, throwError, timer } from 'rxjs';

import type { Guest } from '@interfaces/guest.interface';
import type { RateGridPreflightRequest, SeasonWrite } from '@interfaces/rate-grid.interface';
import type { SeasonPayload, SeasonRentalAssignment } from '@interfaces/seasons.interface';
import type { HostUser } from '@interfaces/settings.interface';
import type {
  BoAccountUpdateRequest,
  BoBookingWorkflowResponse,
  BoPaymentsUpdateRequest,
  BoWebsiteUpdateRequest,
} from '@services/settings.adapter';
import { datesBetween } from '@utils/rate-grid-date.util';

import {
  createAmenityMock,
  deleteAmenityMock,
  getAmenitiesByCategoryMock,
  getAmenityByIdMock,
  updateAmenityMock,
} from '../mocks/functions/amenity.functions';
import { getHostByCodeMock, loginMock, refreshTokenMock } from '../mocks/functions/auth.functions';
import {
  acceptBookingMock,
  cancelBookingMock,
  createBookingMock,
  deleteBookingMock,
  getBookingByIdMock,
  getBookingWorkflowMock,
  getBookingsByHostIdMock,
  getBookingsByRentalIdMock,
  isChannelManagedBookingMock,
  omitBookingMirMock,
  reactivateBookingMirMock,
  refundBookingMock,
  rejectBookingMock,
  searchBookingsMock,
  updateBookingHolderMock,
  updateBookingMock,
  updateBookingStatusMock,
} from '../mocks/functions/booking.functions';
import {
  getChannexAriStatusMock,
  getChannexHealthMock,
} from '../mocks/functions/channex.functions';
import {
  createContactPersonMock,
  deleteContactPersonMock,
  getContactPersonByIdMock,
  getContactPersonsMock,
  updateContactPersonMock,
} from '../mocks/functions/contact-person.functions';
import {
  guestFlowBooking,
  guestFlowGuests,
  guestFlowManualMir,
  guestFlowSaveGuests,
  guestFlowSubmit,
  guestFlowWorkflow,
} from '../mocks/functions/guest-flow.functions';
import {
  createGuestMock,
  deleteGuestMock,
  getAllGuestsMock,
  getGuestByIdMock,
  getGuestsByBookingIdMock,
  submitCheckinMock,
  updateGuestMock,
  updateGuestsByBookingMock,
} from '../mocks/functions/guest.functions';
import { getHostByIdMock, getHostMock, updateHostMock } from '../mocks/functions/host.functions';
import {
  createLocationMock,
  deleteLocationMock,
  getAllLocationsMock,
  getCitiesMock,
  getCityByIdMock,
  getCountriesMock,
  getLocationByIdMock,
  getLocationsByRentalIdMock,
  getProvincesMock,
  updateLocationMock,
} from '../mocks/functions/location.functions';
import {
  createAccessCodeByRentalIdMock,
  createLockMock,
  deleteAccessCodeByRentalIdMock,
  deleteLockMock,
  getAccessCodesByLockIdMock,
  getAccessCodesByRentalIdMock,
  getAccessHistoryByLockIdMock,
  getAccessHistoryByRentalIdMock,
  getAllLocksMock,
  getBridgeStatusByRentalIdMock,
  getLockBatteryByRentalIdMock,
  getLockByIdMock,
  getLocksByRentalIdMock,
  lockByRentalIdMock,
  unlockByRentalIdMock,
  updateLockMock,
} from '../mocks/functions/lock.functions';
import {
  getActivityOverviewByRentalIdMock,
  getActivityOverviewMock,
} from '../mocks/functions/metrics.functions';
import { RateGridMockError } from '../mocks/functions/rate-grid-mock.model';
import {
  MAX_RANGE_NIGHTS,
  commitRateGridMock,
  getRateGridBookingSummaryMock,
  getRateGridHistoryMock,
  getRateGridOperationMock,
  getRateGridRangeMock,
  getRateGridSeasonsMock,
  preflightRateGridMock,
  retryRateGridPublicationMock,
  saveRateGridSeasonMock,
} from '../mocks/functions/rate-grid.functions';
import { handleRefundsMock } from '../mocks/functions/refund.functions';
import {
  MOCK_RENTAL_IDS,
  getErrorScenario,
  getRentalSectionsByIdMock,
} from '../mocks/functions/rental-sections.functions';
import {
  createRentalMock,
  createRentalWizardMock,
  deleteRentalMock,
  getPublishRefusalMock,
  getRentalByIdMock,
  getRentalTypesMock,
  patchRentalContentMock,
  searchRentalsMock,
  updateRentalMock,
} from '../mocks/functions/rental.functions';
import {
  type SeasonMockResult,
  assignSeasonRentalsMock,
  createSeasonMock,
  duplicateSeasonMock,
  getSeasonCalendarMock,
  getSeasonsMock,
  removeSeasonMock,
  updateSeasonMock,
} from '../mocks/functions/seasons.functions';
import {
  createStripeAccountLinkMock,
  createWebsiteFaqMock,
  deleteUserMock,
  deleteWebsiteFaqMock,
  getAccountSettingsMock,
  getBookingWorkflowSettingsMock,
  getPaymentsSettingsMock,
  getSettingsSummaryMock,
  getUsersSettingsMock,
  getWebsiteLegalBlocksMock,
  getWebsiteSettingsMock,
  saveLegalTextMock,
  testPaymentMock,
  updateAccountSettingsMock,
  updateBookingWorkflowSettingsMock,
  updatePaymentsSettingsMock,
  updateWebsiteFaqMock,
  updateWebsiteSettingsMock,
  upsertUserMock,
} from '../mocks/functions/settings.functions';
import {
  createSpaceMock,
  createSpaceTypeMock,
  deleteSpaceMock,
  getAllSpacesMock,
  getSpaceByIdMock,
  getSpaceTypesMock,
  getSpacesByRentalIdMock,
  updateSpaceMock,
} from '../mocks/functions/space.functions';
import {
  MOCK_ACCESS_METRICS,
  MOCK_ACCESS_METRICS_OVERAL_VIEW,
  MOCK_PERFORMANCE_METRICS,
} from '../mocks/metrics.mock';
import { mockDb } from '../mocks/mock-db';

const MOCK_DELAY = 300;

const USE_MOCKS_KEY = 'useMocks';
const MOCK_SCOPES_KEY = 'mockScopes';

export const MOCK_SCOPES = [
  'settings',
  'feondi',
  'rateGrid',
  'channex',
  'metrics',
  'auth',
  'host',
  'rentals',
  'bookings',
  'guests',
  'locations',
  'locks',
  'spaces',
  'amenities',
  'contacts',
] as const;

export type MockScope = (typeof MOCK_SCOPES)[number];

type MockScopeFlags = Record<MockScope, boolean>;

/**
 * Reads a localStorage entry, returning null when storage is unavailable (blocked cookies, private
 * mode) so a failed lookup can never take the request chain down with it.
 */
const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Lets `?useMocks=true` on the page URL switch the mocks on, and `?useMocks=false` switch them off
 * again — the same toggle the guest app has. It persists, so the flag only has to appear once and
 * every later navigation stays in the same mode; a demo can be opened from a link instead of a
 * devtools console.
 */
const applyUrlToggle = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const flag = new URLSearchParams(window.location.search).get(USE_MOCKS_KEY);
    if (flag === 'true') {
      localStorage.setItem(USE_MOCKS_KEY, 'true');
    } else if (flag === 'false') {
      localStorage.removeItem(USE_MOCKS_KEY);
      localStorage.removeItem(MOCK_SCOPES_KEY);
    }
  } catch {
    return;
  }
};

/**
 * Persists the scope flags, ignoring storage failures since the resolved defaults still apply for
 * the rest of the session.
 */
const writeScopes = (scopes: MockScopeFlags): void => {
  try {
    localStorage.setItem(MOCK_SCOPES_KEY, JSON.stringify(scopes, null, 2));
  } catch {
    return;
  }
};

/**
 * Resolves the per-scope toggles, seeding `mockScopes` with every entry in MOCK_SCOPES the first
 * time mocks are switched on so the flags are discoverable and editable in devtools. Entries that
 * are absent or malformed are merged back in as enabled — only an explicit `false` opts a scope out
 * and sends that group of endpoints to the real backend.
 */
const readScopes = (): MockScopeFlags => {
  let stored: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(readStorage(MOCK_SCOPES_KEY) ?? '{}');
    if (parsed && typeof parsed === 'object') {
      stored = parsed as Record<string, unknown>;
    }
  } catch {
    stored = {};
  }

  const scopes = {} as MockScopeFlags;
  let seeded = true;
  for (const scope of MOCK_SCOPES) {
    scopes[scope] = stored[scope] !== false;
    if (typeof stored[scope] !== 'boolean') {
      seeded = false;
    }
  }

  if (!seeded) {
    writeScopes(scopes);
  }

  return scopes;
};

/**
 * Maps a request URL to the scope whose handlers below would serve it. Order mirrors the handler
 * chain rather than MOCK_SCOPES: the metric endpoints are matched first because the rental and lock
 * blocks fall through to them. Unknown URLs resolve to null and always reach the real backend.
 */
const scopeOf = (url: string): MockScope | null => {
  if (url.includes('/settings/') || url.endsWith('/settings')) {
    return 'settings';
  }
  if (url.includes('/feondi/config/') || url.includes('/feondi/reference/')) {
    return 'feondi';
  }
  if (url.includes('/channex/')) {
    return 'channex';
  }
  if (url.includes('/rate-grid')) {
    return 'rateGrid';
  }
  if (
    url.includes('/activity-overview') ||
    url.includes('/access-metrics') ||
    url.includes('/performance-metrics')
  ) {
    return 'metrics';
  }
  if (url.includes('/auth/')) {
    return 'auth';
  }
  if (url.includes('/contact-persons') || url.includes('/contactpersons')) {
    return 'contacts';
  }
  if (url.includes('/amenities')) {
    return 'amenities';
  }
  if (url.includes('/spaces') || url.includes('/spacetypes')) {
    return 'spaces';
  }
  if (url.includes('/lock')) {
    return 'locks';
  }
  if (url.includes('/locations') || url.includes('/location/')) {
    return 'locations';
  }
  if (url.includes('/guests') || url.includes('/checkin/')) {
    return 'guests';
  }
  if (
    url.includes('/booking') ||
    url.includes('/acceptBooking/') ||
    url.includes('/cancel-booking/') ||
    url.includes('/refund-booking/')
  ) {
    return 'bookings';
  }
  if (url.includes('/rental')) {
    return 'rentals';
  }
  if (url.includes('/host')) {
    return 'host';
  }
  return null;
};

/**
 * Shims the endpoints the backend does not implement yet (Host Settings + Feondi config/reference)
 * and the wider mock suite used for offline/demo runs. Opt in per browser with
 * `localStorage.setItem('useMocks', 'true')`; that seeds a `mockScopes` object listing every scope
 * enabled, so setting one to `false` sends just those endpoints to the real backend. With `useMocks`
 * unset every request passes straight through and `mockScopes` is ignored. PRE and production
 * compile this interceptor out of the app entirely.
 */
export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  applyUrlToggle();

  if (readStorage(USE_MOCKS_KEY) !== 'true') {
    return next(req);
  }

  const scopes = readScopes();
  const scope = scopeOf(req.url);
  if (!scope || !scopes[scope]) {
    return next(req);
  }

  const refundsShim = handleRefundsMock(req);
  if (refundsShim) {
    return refundsShim;
  }

  const url = req.url;
  const method = req.method;

  const getIdFromUrl = (pattern: string): string | null => {
    const regex = new RegExp(`${pattern}/([^/]+)$`);
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const mockResponse = <T>(data: T) => {
    return of(new HttpResponse({ status: 200, body: data })).pipe(delay(MOCK_DELAY));
  };

  // `delay` never delays errors, and an instant refusal is one no real backend can produce — the
  // resource machinery must see the request leave and come back, so the error waits like a body does.
  const delayedError = (toError: () => HttpErrorResponse) => {
    return timer(MOCK_DELAY).pipe(mergeMap(() => throwError(toError)));
  };

  const mockErrorResponse = (status: number, message: string) => {
    return delayedError(
      () =>
        new HttpErrorResponse({
          error: { error: message, message },
          status,
          statusText: message,
          url: req.url,
        })
    );
  };

  /**
   * A refusal in a coded shape, so the client maps it to the copy for that code rather than to
   * the generic failure line.
   */
  const mockCodedError = (status: number, code: string, details: Record<string, unknown> = {}) => {
    return delayedError(
      () =>
        new HttpErrorResponse({
          error: {
            errorCode: code,
            message: `mocked ${code}`,
            correlationId: crypto.randomUUID(),
            details,
          },
          status,
          statusText: code,
          url: req.url,
        })
    );
  };

  // ---- Host Settings façade + Feondi channel writes ----
  if (/\/settings(\?.*)?$/.test(url) && method === 'GET') {
    return mockResponse(getSettingsSummaryMock());
  }

  const seasonResult = (result: SeasonMockResult) =>
    'refusal' in result
      ? mockCodedError(result.refusal.status, result.refusal.code, result.refusal.details)
      : mockResponse(result.season);
  const ifMatch = req.headers.get('If-Match');

  const calendarRentalId = /\/rentals\/([^/?]+)\/seasons\/calendar/.exec(url)?.[1];
  if (calendarRentalId && method === 'GET') {
    const result = getSeasonCalendarMock(
      calendarRentalId,
      req.params.get('from'),
      req.params.get('to')
    );
    return 'refusal' in result
      ? mockCodedError(result.refusal.status, result.refusal.code, result.refusal.details)
      : mockResponse(result.calendar);
  }

  if (url.includes('/settings/seasons/') && url.endsWith('/duplicate') && method === 'POST') {
    const seasonId = /\/settings\/seasons\/([^/]+)\/duplicate$/.exec(url)?.[1] ?? '';
    return seasonResult(duplicateSeasonMock(seasonId, ifMatch));
  }

  if (url.includes('/settings/seasons/') && url.endsWith('/rentals') && method === 'PUT') {
    const seasonId = /\/settings\/seasons\/([^/]+)\/rentals$/.exec(url)?.[1] ?? '';
    return seasonResult(
      assignSeasonRentalsMock(seasonId, req.body as SeasonRentalAssignment, ifMatch)
    );
  }

  if (url.includes('/settings/seasons')) {
    const seasonId = getIdFromUrl('seasons');
    if (method === 'GET') {
      return mockResponse(getSeasonsMock());
    }
    if (method === 'POST') {
      return seasonResult(
        createSeasonMock(req.body as SeasonPayload, req.headers.get('Idempotency-Key'))
      );
    }
    if (seasonId && method === 'PATCH') {
      return seasonResult(updateSeasonMock(seasonId, req.body as SeasonPayload, ifMatch));
    }
    if (seasonId && method === 'DELETE') {
      const refusal = removeSeasonMock(seasonId, ifMatch);
      return refusal
        ? mockCodedError(refusal.status, refusal.code, refusal.details)
        : mockResponse(null);
    }
  }

  if (url.includes('/settings/account')) {
    if (method === 'GET') {
      return mockResponse(getAccountSettingsMock());
    }
    if (method === 'PATCH') {
      return mockResponse(updateAccountSettingsMock(req.body as BoAccountUpdateRequest));
    }
  }

  if (url.includes('/settings/website/legal-blocks') && method === 'GET') {
    return mockResponse(getWebsiteLegalBlocksMock());
  }

  if (url.includes('/settings/website/legal/') && method === 'PATCH') {
    const type = url.split('/settings/website/legal/').pop()?.split('?')[0] ?? '';
    const locale = new URL(url, 'http://localhost').searchParams.get('locale') ?? '';
    return mockResponse(
      saveLegalTextMock(type, locale, req.body as { content?: string; enabled?: boolean })
    );
  }

  if (url.includes('/settings/website/faqs')) {
    const faqId = getIdFromUrl('faqs');
    if (method === 'POST') {
      return mockResponse(createWebsiteFaqMock(req.body as Record<string, unknown>));
    }
    if (faqId && method === 'PATCH') {
      return mockResponse(updateWebsiteFaqMock(faqId, req.body as Record<string, unknown>));
    }
    if (faqId && method === 'DELETE') {
      deleteWebsiteFaqMock(faqId);
      return mockResponse(null);
    }
  }

  if (url.includes('/settings/website/publish') && method === 'POST') {
    return mockResponse({ status: 'PUBLISHING', lastPublishedAt: null, projectionVersion: 1 });
  }

  if (url.includes('/settings/website')) {
    if (method === 'GET') {
      return mockResponse(getWebsiteSettingsMock());
    }
    if (method === 'PATCH') {
      return mockResponse(updateWebsiteSettingsMock(req.body as BoWebsiteUpdateRequest));
    }
  }

  if (url.includes('/settings/payments/test') && method === 'POST') {
    return mockResponse(testPaymentMock((req.body as { provider?: string })?.provider ?? ''));
  }

  if (url.includes('/settings/payments/stripe/account-link') && method === 'POST') {
    return mockResponse(createStripeAccountLinkMock());
  }

  if (url.includes('/settings/payments')) {
    if (method === 'GET') {
      return mockResponse(getPaymentsSettingsMock());
    }
    if (method === 'PATCH') {
      return mockResponse(updatePaymentsSettingsMock(req.body as BoPaymentsUpdateRequest));
    }
  }

  if (url.includes('/settings/booking-workflow')) {
    if (method === 'GET') {
      return mockResponse(getBookingWorkflowSettingsMock());
    }
    if (method === 'PATCH') {
      return mockResponse(updateBookingWorkflowSettingsMock(req.body as BoBookingWorkflowResponse));
    }
  }

  if (url.includes('/settings/users')) {
    if (method === 'GET') {
      return mockResponse(getUsersSettingsMock());
    }
    if (method === 'PUT') {
      return mockResponse(upsertUserMock(req.body as HostUser));
    }
    if (method === 'DELETE') {
      const userName = url.split('/settings/users/').pop() ?? '';
      deleteUserMock(userName);
      return mockResponse(null);
    }
  }

  // ---- Channex: the rental-level operational reads the backoffice can actually ask for ----
  if (url.includes('/channex/rentals/')) {
    const rentalId = url.split('/channex/rentals/')[1]?.split(/[/?]/)[0] ?? '';

    if (url.includes('/health') && method === 'GET') {
      return mockResponse(getChannexHealthMock(rentalId));
    }

    if (url.includes('/ari/status') && method === 'GET') {
      const status = getChannexAriStatusMock(rentalId);
      // The real read resolves its scope with `resolveMapped`, so an unmapped rental is a 404 and
      // not an empty reading. The mock refuses the same way to keep the calendar honest.
      return status
        ? mockResponse(status)
        : mockCodedError(404, 'CHANNEX_PROPERTY_MAPPING_NOT_FOUND');
    }

    return mockErrorResponse(404, `Unknown channex route: ${method} ${url}`);
  }

  // ---- Rate Grid: GET /rentals/{id}/rate-grid and everything beneath it ----
  if (url.includes('/rate-grid')) {
    const rentalId = /\/rentals\/([^/?]+)\/rate-grid/.exec(url)?.[1] ?? '';
    const idempotencyKey = req.headers.get('Idempotency-Key') ?? '';

    try {
      if (method === 'GET') {
        const bookingDate = /\/rate-grid\/days\/([^/?]+)\/booking-summary/.exec(url)?.[1];
        if (bookingDate) {
          return mockResponse(getRateGridBookingSummaryMock(rentalId, bookingDate));
        }
        const operationId = /\/rate-grid\/operations\/([^/?]+)$/.exec(url)?.[1];
        if (operationId) {
          return mockResponse(getRateGridOperationMock(rentalId, operationId));
        }
        if (url.includes('/rate-grid/seasons')) {
          return mockResponse(getRateGridSeasonsMock(rentalId));
        }
        if (url.includes('/rate-grid/history')) {
          return mockResponse(
            getRateGridHistoryMock(
              rentalId,
              Number(req.params.get('page') ?? 0),
              Number(req.params.get('size') ?? 20)
            )
          );
        }
        const from = req.params.get('from') ?? '';
        const to = req.params.get('to') ?? '';
        const nights = datesBetween(from, to).length;
        if (nights === 0 || nights > MAX_RANGE_NIGHTS) {
          return mockErrorResponse(
            422,
            `A read covers 1 to ${MAX_RANGE_NIGHTS} nights, asked ${nights}`
          );
        }
        return mockResponse(getRateGridRangeMock(rentalId, from, to));
      }

      if (method === 'POST') {
        const retryFor = /\/rate-grid\/operations\/([^/?]+)\/retry$/.exec(url)?.[1];
        if (retryFor) {
          return mockResponse(retryRateGridPublicationMock(rentalId, retryFor, idempotencyKey));
        }
        if (url.includes('/rate-grid/operations/preflight')) {
          return mockResponse(
            preflightRateGridMock(rentalId, req.body as RateGridPreflightRequest)
          );
        }
        if (url.includes('/rate-grid/operations')) {
          const preflightId = (req.body as { preflightId?: string })?.preflightId ?? '';
          return mockResponse(commitRateGridMock(rentalId, preflightId, idempotencyKey));
        }
        if (url.includes('/rate-grid/seasons')) {
          return mockResponse(saveRateGridSeasonMock(rentalId, req.body as SeasonWrite));
        }
      }
    } catch (error) {
      if (error instanceof RateGridMockError) {
        return mockCodedError(error.status, error.code);
      }
      throw error;
    }

    return mockErrorResponse(404, `Unknown rate-grid route: ${method} ${url}`);
  }

  if (url.includes('/auth/externalCodeName/') || url.includes('/auth/host/validate')) {
    const externalCodeName = url.split('/').pop() ?? '';
    const result = getHostByCodeMock(externalCodeName);
    return result ? mockResponse(result) : mockErrorResponse(404, 'Host not found');
  }

  if (url.includes('/auth/token')) {
    const body = req.body as {
      externalCodeName: string;
      username: string;
      password: string;
    };

    const result = loginMock(body);
    if (result) {
      return mockResponse(result);
    }

    return mockErrorResponse(401, 'Invalid credentials');
  }

  if (url.includes('/auth/refresh')) {
    return mockResponse(refreshTokenMock());
  }

  if (url.includes('/api/bo/v1/hosts') && method === 'GET' && !/\/hosts\/[^/]+/.exec(url)) {
    return mockResponse(getHostMock());
  }

  if (url.includes('/host') && !url.includes('/auth/host')) {
    const id = getIdFromUrl('host');

    if (id && method === 'GET') {
      return mockResponse(getHostByIdMock(id));
    }

    if (id && method === 'PUT') {
      return mockResponse(updateHostMock(id, req.body as Record<string, unknown>));
    }
  }

  if (url.includes('/rentalWizard/') && method === 'POST') {
    return mockResponse(createRentalWizardMock());
  }

  // Manual publish returns 200 without a body on success (correlation travels in the header);
  // scenario rentals answer with the matching refusal.
  if (/\/rental\/publish\/([^/]+)$/.test(url) && method === 'PUT') {
    const rentalId = /\/rental\/publish\/([^/]+)$/.exec(url)?.[1] ?? '';
    const refusal = getPublishRefusalMock(rentalId);
    if (refusal) {
      return mockCodedError(refusal.status, refusal.code, refusal.details);
    }
    return of(
      new HttpResponse({
        status: 200,
        body: null,
        headers: new HttpHeaders({ 'X-Correlation-Id': crypto.randomUUID() }),
      })
    ).pipe(delay(MOCK_DELAY));
  }

  if (url.includes('/rental') && !url.includes('/rentals') && !url.includes('/rentalWizard')) {
    const idMatch = /\/rental\/([^/]+)$/.exec(url);
    const id = idMatch ? idMatch[1] : null;

    if (method === 'POST' && !id) {
      return mockResponse(createRentalMock(req.body as Partial<Record<string, unknown>>));
    }

    if (id && method === 'PUT') {
      return mockResponse(updateRentalMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteRentalMock());
    }
  }

  if (url.includes('/rentals')) {
    const sectionsIdMatch = /\/rentals\/([^/]+)\/sections/.exec(url);
    if (sectionsIdMatch && method === 'GET') {
      const rentalId = sectionsIdMatch[1];

      const errorScenario = getErrorScenario(rentalId);
      if (errorScenario) {
        if (rentalId === MOCK_RENTAL_IDS.TIMEOUT) {
          return timer(5000).pipe(
            mergeMap(() =>
              throwError(
                () =>
                  new HttpErrorResponse({
                    error: { error: errorScenario.message, message: errorScenario.message },
                    status: errorScenario.status,
                    statusText: 'Request Timeout',
                    url: req.url,
                  })
              )
            )
          );
        }
        return mockErrorResponse(errorScenario.status, errorScenario.message);
      }

      return mockResponse(getRentalSectionsByIdMock(rentalId));
    }

    const id = getIdFromUrl('rentals');

    if (id && method === 'GET') {
      const rental = getRentalByIdMock(id);
      return rental ? mockResponse(rental) : mockErrorResponse(404, 'Rental not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateRentalMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'PATCH') {
      const result = patchRentalContentMock(id, (req.body ?? {}) as Record<string, unknown>);
      if ('refusal' in result) {
        return mockCodedError(result.refusal.status, result.refusal.code, result.refusal.details);
      }
      return mockResponse(result.rental);
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteRentalMock());
    }

    if (method === 'POST') {
      const body = (req.body ?? { page: 0, size: 20 }) as Criteria;
      return mockResponse(searchRentalsMock(body));
    }
  }

  const guestFlowId =
    /\/(?:booking|bookings|guests|guestsByBookingId|checkin)\/(guest-flow-[^/?]+)/.exec(url)?.[1];
  if (guestFlowId) {
    if (url.includes('/workflow/manual-mir') && method === 'POST') {
      const manual = (req.body as { manualMirManaged?: boolean } | null)?.manualMirManaged;
      const result = guestFlowManualMir(guestFlowId, manual !== false);
      return typeof result === 'string' ? mockCodedError(409, result) : mockResponse(result);
    }
    if (/\/workflow(\?.*)?$/.test(url) && method === 'GET') {
      return mockResponse(guestFlowWorkflow(guestFlowId));
    }
    if (url.endsWith('/snapshot') && method === 'GET') {
      return mockResponse({ revision: 0, guests: guestFlowGuests(guestFlowId) });
    }
    if (url.includes('/guestsByBookingId/') && method === 'GET') {
      return mockResponse(guestFlowGuests(guestFlowId));
    }
    if (url.includes('/guests/') && method === 'PUT') {
      const refusal = guestFlowSaveGuests(
        guestFlowId,
        (req.body as { guests?: Guest[] } | null)?.guests ?? []
      );
      return refusal ? mockCodedError(409, refusal) : mockResponse(null);
    }
    if (url.includes('/checkin/') && method === 'POST') {
      const refusal = guestFlowSubmit(guestFlowId);
      return refusal ? mockCodedError(409, refusal) : mockResponse(null);
    }
    if (method === 'GET') {
      return mockResponse(guestFlowBooking(guestFlowId));
    }
  }

  if (url.includes('/booking/') && !url.includes('/bookings')) {
    const id = getIdFromUrl('booking');

    if (id && method === 'GET') {
      const booking = getBookingByIdMock(id);
      return booking ? mockResponse(booking) : mockErrorResponse(404, 'Booking not found');
    }
  }

  /**
   * Refuses, with the backend's 409, the mutations it guards on a channel booking: holder,
   * status, accept/reject, cancel and refund. Guests and check-in stay open, as in the backend.
   */
  const channelReadOnly = (bookingId: string) =>
    isChannelManagedBookingMock(bookingId)
      ? mockCodedError(409, 'CHANNEL_BOOKING_READ_ONLY')
      : null;

  if (url.includes('/bookingholder/')) {
    const id = url.split('/bookingholder/').pop();

    if (id && method === 'PUT') {
      return channelReadOnly(id) ?? mockResponse(updateBookingHolderMock(id, req.body as object));
    }
  }

  if (url.includes('/bookingstatus/') && method === 'PUT') {
    const bookingId = url.split('/bookingstatus/').pop() ?? '';
    return (
      channelReadOnly(bookingId) ??
      mockResponse(updateBookingStatusMock(bookingId, req.body as { bookingStatus: string }))
    );
  }

  if (url.includes('/acceptBooking/') && method === 'POST') {
    const id = url.split('/acceptBooking/').pop() ?? '';
    const isAccepted = (req.body as { isAccepted?: boolean })?.isAccepted ?? true;
    const refused = channelReadOnly(id);
    if (refused) {
      return refused;
    }
    if (isAccepted) {
      return mockResponse(acceptBookingMock(id));
    } else {
      return mockResponse(rejectBookingMock(id));
    }
  }

  if (url.includes('/cancel-booking/') && method === 'POST') {
    const id = url.split('/cancel-booking/').pop() ?? '';
    return channelReadOnly(id) ?? mockResponse(cancelBookingMock(id));
  }

  if (url.includes('/refund-booking/') && method === 'POST') {
    const id = url.split('/refund-booking/').pop() ?? '';
    const body = req.body as {
      originalAmount?: number;
      penaltyCharges?: number;
      refundReason?: string;
    };
    return channelReadOnly(id) ?? mockResponse(refundBookingMock(id, body));
  }

  const workflowBookingId = /\/bookings\/([^/?]+)\/workflow(\/manual-mir)?(\?.*)?$/.exec(url);
  if (workflowBookingId) {
    const [, bookingId, manual] = workflowBookingId;
    if (manual && method === 'POST') {
      if (mockDb.host.role !== 'OWNER') {
        return mockCodedError(403, 'BOOKING_MUTATION_FORBIDDEN');
      }
      // `manualMirManaged: false` requests reactivation; omitting it or `true` keeps the skip.
      if ((req.body as { manualMirManaged?: boolean } | null)?.manualMirManaged === false) {
        const result = reactivateBookingMirMock(bookingId);
        if (result.errorCode) {
          return mockCodedError(result.status, result.errorCode);
        }
        if (result.status === 404 || !result.workflow) {
          return mockErrorResponse(404, 'Booking not found');
        }
        return of(new HttpResponse({ status: result.status, body: result.workflow })).pipe(
          delay(MOCK_DELAY)
        );
      }
      const omitted = omitBookingMirMock(bookingId);
      return omitted ? mockResponse(omitted) : mockErrorResponse(404, 'Booking not found');
    }
    if (!manual && method === 'GET') {
      const workflow = getBookingWorkflowMock(bookingId);
      return workflow ? mockResponse(workflow) : mockErrorResponse(404, 'Booking not found');
    }
    return mockErrorResponse(404, 'Booking not found');
  }

  if (url.includes('/bookings-by-rental') && method === 'GET') {
    const rentalId = new URL(url, 'http://localhost').searchParams.get('rentalId') ?? '';
    return mockResponse(getBookingsByRentalIdMock(rentalId));
  }

  if (url.includes('/bookings-by-host') && method === 'GET') {
    return mockResponse(getBookingsByHostIdMock());
  }

  if (url.includes('/bookings')) {
    const id = getIdFromUrl('bookings');

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createBookingMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const booking = getBookingByIdMock(id);
      return booking ? mockResponse(booking) : mockErrorResponse(404, 'Booking not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateBookingMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteBookingMock());
    }

    if (method === 'POST') {
      const body = (req.body ?? { page: 0, size: 20 }) as Criteria;
      return mockResponse(searchBookingsMock(body));
    }
  }

  if (url.includes('/guestsByBookingId/') && method === 'GET') {
    const bookingId = url.split('/guestsByBookingId/').pop() ?? '';
    return mockResponse(getGuestsByBookingIdMock(bookingId));
  }

  if (url.includes('/checkin/') && method === 'POST') {
    const bookingId = url.split('/checkin/').pop() ?? '';
    submitCheckinMock(bookingId);
    return mockResponse(null);
  }

  const snapshotMatch = /\/guests\/([^/]+)\/snapshot$/.exec(url);
  if (snapshotMatch && method === 'GET') {
    return mockResponse({ revision: 0, guests: getGuestsByBookingIdMock(snapshotMatch[1]) });
  }

  if (url.includes('/guests')) {
    const id = getIdFromUrl('guests');

    if (url.includes('/byBooking/') && method === 'PUT') {
      const bookingId = url.split('/byBooking/').pop() ?? '';
      return mockResponse(updateGuestsByBookingMock(bookingId, req.body as { guests?: Guest[] }));
    }

    if (id && method === 'PUT') {
      return mockResponse(updateGuestsByBookingMock(id, req.body as { guests?: Guest[] }));
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createGuestMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const guest = getGuestByIdMock(Number(id));
      return guest ? mockResponse(guest) : mockErrorResponse(404, 'Guest not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateGuestMock(Number(id), req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteGuestMock());
    }

    return mockResponse(getAllGuestsMock());
  }

  if (
    url.includes('/locations') ||
    url.includes('/location/countries') ||
    url.includes('/location/provinces') ||
    url.includes('/location/cities')
  ) {
    if (url.includes('/location/countries')) {
      return mockResponse(getCountriesMock());
    }

    if (url.includes('/location/provinces')) {
      return mockResponse(getProvincesMock());
    }

    if (/\/location\/cities\/[^?]+/.exec(url) && method === 'GET') {
      const cityId = url.split('/location/cities/').pop()?.split('?')[0] ?? '';
      const city = getCityByIdMock(cityId);
      return city ? mockResponse(city) : mockErrorResponse(404, 'City not found');
    }

    if (url.includes('/location/cities')) {
      const provinceCode = req.params.get('provinceCode');
      return mockResponse(getCitiesMock(provinceCode ?? undefined));
    }

    const id = getIdFromUrl('locations');

    if (url.includes('/locationsByRentalId/')) {
      const rentalId = url.split('/locationsByRentalId/').pop() ?? '';
      return mockResponse(getLocationsByRentalIdMock(rentalId));
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createLocationMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const location = getLocationByIdMock(id);
      return location ? mockResponse(location) : mockErrorResponse(404, 'Location not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateLocationMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteLocationMock());
    }

    return mockResponse(getAllLocationsMock());
  }

  if (url.includes('/lock/') && !url.includes('/locks')) {
    const rentalId = /\/lock\/([^/]+)/.exec(url)?.[1] ?? '';

    if (url.includes('/devices') && method === 'GET') {
      return mockResponse(getLocksByRentalIdMock(rentalId));
    }

    if (url.includes('/battery') && method === 'GET') {
      return mockResponse(getLockBatteryByRentalIdMock(rentalId));
    }

    if (url.includes('/codes') && method === 'GET') {
      return mockResponse(getAccessCodesByRentalIdMock(rentalId));
    }

    if (/\/codes\/[^/]+$/.exec(url) && method === 'POST') {
      const passcodeId = url.split('/codes/').pop() ?? '';
      return mockResponse(deleteAccessCodeByRentalIdMock(rentalId, passcodeId));
    }

    if (url.includes('/codes') && method === 'POST') {
      return mockResponse(createAccessCodeByRentalIdMock(rentalId));
    }

    if (url.includes('/history') && method === 'GET') {
      return mockResponse(
        getAccessHistoryByRentalIdMock(
          rentalId,
          Number(req.params.get('page') ?? 0),
          Number(req.params.get('size') ?? 20)
        )
      );
    }

    if (url.includes('/unlock') && method === 'POST') {
      return mockResponse(unlockByRentalIdMock(rentalId));
    }

    if (/\/lock\/[^/]+\/lock$/.test(url) && method === 'POST') {
      return mockResponse(lockByRentalIdMock(rentalId));
    }

    if (url.includes('/status') && method === 'GET') {
      return mockResponse(getBridgeStatusByRentalIdMock(rentalId));
    }
    if (url.includes('/devices') && method === 'GET') {
      return mockResponse(getAllLocksMock());
    }
  }

  if (url.includes('/locks')) {
    const id = getIdFromUrl('locks');

    if (url.includes('/byRental/') && method === 'GET' && !url.includes('/access-')) {
      const rentalId = url.split('/byRental/').pop()?.split('/')[0] ?? '';
      return mockResponse(getLocksByRentalIdMock(rentalId));
    }

    if (url.includes('/access-codes')) {
      const lockId = /\/locks\/([^/]+)\/access-codes/.exec(url)?.[1] ?? '';
      return mockResponse(getAccessCodesByLockIdMock(lockId));
    }

    if (url.includes('/access-history')) {
      const lockId = /\/locks\/([^/]+)\/access-history/.exec(url)?.[1] ?? '';
      return mockResponse(getAccessHistoryByLockIdMock(lockId));
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createLockMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      return mockResponse(getLockByIdMock(id));
    }

    if (id && method === 'PUT') {
      return mockResponse(updateLockMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteLockMock());
    }

    return mockResponse(getAllLocksMock());
  }

  if (url.includes('/spaces') || url.includes('/spacetypes') || url.includes('/spacesByRentalId')) {
    const id = getIdFromUrl('spaces');

    if (url.includes('/spacetypes')) {
      const spaceTypeId = getIdFromUrl('spacetypes');
      if (spaceTypeId && method === 'PUT') {
        return mockResponse(createSpaceTypeMock({ name: (req.body as { name: string }).name }));
      }
      if (spaceTypeId && method === 'DELETE') {
        return mockResponse(deleteSpaceMock());
      }
      if (method === 'POST') {
        return mockResponse(createSpaceTypeMock({ name: (req.body as { name: string }).name }));
      }
      return mockResponse(getSpaceTypesMock());
    }

    if (url.includes('/spacesByRentalId/')) {
      const rentalId = url.split('/spacesByRentalId/').pop() ?? '';
      return mockResponse(getSpacesByRentalIdMock(rentalId));
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createSpaceMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const space = getSpaceByIdMock(id);
      return space ? mockResponse(space) : mockErrorResponse(404, 'Space not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateSpaceMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteSpaceMock());
    }

    return mockResponse(getAllSpacesMock());
  }

  if (url.includes('/rentaltypes')) {
    if (method === 'GET') {
      return mockResponse(getRentalTypesMock());
    }
  }

  if (url.includes('/amenities')) {
    const id = getIdFromUrl('amenities');

    if (method === 'GET' && !id) {
      return mockResponse(getAmenitiesByCategoryMock());
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createAmenityMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const amenity = getAmenityByIdMock(id);
      return amenity ? mockResponse(amenity) : mockErrorResponse(404, 'Amenity not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateAmenityMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteAmenityMock());
    }

    return mockResponse(getAmenitiesByCategoryMock());
  }

  if (url.includes('/contact-persons') || url.includes('/contactpersons')) {
    const id = getIdFromUrl('contact-persons') ?? getIdFromUrl('contactpersons');

    if (method === 'GET' && !id) {
      return mockResponse(getContactPersonsMock());
    }

    if (method === 'POST' && url.includes('/create')) {
      return mockResponse(createContactPersonMock(req.body as Record<string, unknown>));
    }

    if (id && method === 'GET') {
      const person = getContactPersonByIdMock(id);
      return person ? mockResponse(person) : mockErrorResponse(404, 'Contact person not found');
    }

    if (id && method === 'PUT') {
      return mockResponse(updateContactPersonMock(id, req.body as Record<string, unknown>));
    }

    if (id && method === 'DELETE') {
      return mockResponse(deleteContactPersonMock());
    }

    return mockResponse(getContactPersonsMock());
  }

  if (
    url.includes('/home') ||
    url.includes('/rentals') ||
    url.includes('/rental/') ||
    url.includes('/lock')
  ) {
    const isLockAccess = /\/lock\/.*\/access-metrics/.test(url);

    const rentalActivityMatch = /\/rental\/([^/]+)\/home\/activity-overview/.exec(url);
    if (rentalActivityMatch) {
      return mockResponse(getActivityOverviewByRentalIdMock(rentalActivityMatch[1]));
    }

    if (url.includes('/activity-overview')) {
      return mockResponse(getActivityOverviewMock());
    }
    if (isLockAccess) {
      return mockResponse(MOCK_ACCESS_METRICS_OVERAL_VIEW());
    }
    if (url.includes('/home/access-metrics')) {
      return mockResponse(MOCK_ACCESS_METRICS());
    }
    if (url.includes('/performance-metrics')) {
      return mockResponse(MOCK_PERFORMANCE_METRICS());
    }
  }

  if (url.includes('/assets/i18n/')) {
    return next(req);
  }

  return next(req);
};
